import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MessageSquare, AlertCircle, CheckCircle, Clock, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Complaint {
  id: string;
  user_id: string;
  complaint_type: string;
  severity: string;
  description: string;
  status: string;
  assigned_to: string | null;
  resolution_notes: string | null;
  created_at: string;
  resolved_at: string | null;
  display_name?: string | null;
}

export function ComplaintsManager() {
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");

  useEffect(() => {
    loadComplaints();
    
    // Subscribe to new complaints
    const channel = supabase
      .channel('complaints-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_complaints'
        },
        () => {
          loadComplaints();
          toast.info('New complaint received');
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadComplaints = async () => {
    try {
      const { data, error } = await supabase
        .from('user_complaints')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch profiles separately
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(c => c.user_id))];
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', userIds);
        
        const profilesMap = new Map(profilesData?.map(p => [p.user_id, p.display_name]) || []);
        const complaintsWithProfiles = data.map(c => ({
          ...c,
          display_name: profilesMap.get(c.user_id) || 'Unknown User'
        }));
        setComplaints(complaintsWithProfiles);
      } else {
        setComplaints([]);
      }
    } catch (error) {
      console.error('Error loading complaints:', error);
      toast.error('Failed to load complaints');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (complaintId: string, newStatus: string) => {
    try {
      const updates: any = { 
        status: newStatus,
        updated_at: new Date().toISOString()
      };
      
      if (newStatus === 'resolved') {
        const { data: { user } } = await supabase.auth.getUser();
        updates.resolved_at = new Date().toISOString();
        updates.assigned_to = user?.id;
      }

      const { error } = await supabase
        .from('user_complaints')
        .update(updates)
        .eq('id', complaintId);

      if (error) throw error;

      toast.success('Status updated');
      loadComplaints();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleResolve = async () => {
    if (!selectedComplaint || !resolutionNotes.trim()) {
      toast.error('Please add resolution notes');
      return;
    }

    setIsResolving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('user_complaints')
        .update({
          status: 'resolved',
          resolution_notes: resolutionNotes,
          resolved_at: new Date().toISOString(),
          assigned_to: user?.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedComplaint.id);

      if (error) throw error;

      toast.success('Complaint resolved');
      setSelectedComplaint(null);
      setResolutionNotes("");
      loadComplaints();
    } catch (error) {
      console.error('Error resolving complaint:', error);
      toast.error('Failed to resolve complaint');
    } finally {
      setIsResolving(false);
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'high': return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'medium': return <Clock className="h-4 w-4 text-primary" />;
      default: return <Clock className="h-4 w-4 text-secondary" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-destructive border-destructive';
      case 'high': return 'text-destructive border-destructive';
      case 'medium': return 'text-primary border-primary';
      default: return 'text-secondary border-secondary';
    }
  };

  const getStatusBadge = (status: string) => {
    const config = {
      pending: { variant: 'outline', label: 'Pending', icon: Clock },
      in_progress: { variant: 'secondary', label: 'In Progress', icon: Clock },
      resolved: { variant: 'default', label: 'Resolved', icon: CheckCircle },
      closed: { variant: 'default', label: 'Closed', icon: CheckCircle }
    };
    const c = config[status as keyof typeof config] || config.pending;
    const Icon = c.icon;
    return (
      <Badge variant={c.variant as any} className="gap-1">
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  const filteredComplaints = filterStatus === 'all' 
    ? complaints 
    : complaints.filter(c => c.status === filterStatus);

  const stats = {
    total: complaints.length,
    pending: complaints.filter(c => c.status === 'pending').length,
    inProgress: complaints.filter(c => c.status === 'in_progress').length,
    resolved: complaints.filter(c => c.status === 'resolved').length
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center">Loading complaints...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Complaints</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold text-primary">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-2xl font-bold text-secondary">{stats.inProgress}</p>
              </div>
              <Clock className="h-8 w-8 text-secondary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Resolved</p>
                <p className="text-2xl font-bold text-accent">{stats.resolved}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-accent" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Complaints List */}
      <Card>
        <CardHeader>
          <CardTitle>User Complaints</CardTitle>
          <CardDescription>
            Manage and resolve user complaints efficiently. Users submit complaints via Settings {'>'} Help & Support. Email confirmations are automatically sent.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={filterStatus} onValueChange={setFilterStatus}>
            <TabsList className="mb-4">
              <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
              <TabsTrigger value="pending">Pending ({stats.pending})</TabsTrigger>
              <TabsTrigger value="in_progress">In Progress ({stats.inProgress})</TabsTrigger>
              <TabsTrigger value="resolved">Resolved ({stats.resolved})</TabsTrigger>
            </TabsList>

            <TabsContent value={filterStatus} className="space-y-4">
              {filteredComplaints.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No complaints in this category</p>
                </div>
              ) : (
                filteredComplaints.map((complaint) => (
                  <Card key={complaint.id} className={`border-l-4 ${getSeverityColor(complaint.severity)}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getSeverityIcon(complaint.severity)}
                            <h4 className="font-semibold capitalize">
                              {complaint.complaint_type.replace(/_/g, ' ')}
                            </h4>
                            {getStatusBadge(complaint.status)}
                          </div>

                          <p className="text-sm text-muted-foreground mb-3">
                            {complaint.description}
                          </p>

                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              <span>
                                {complaint.display_name || 'Unknown User'}
                              </span>
                            </div>
                            <div>
                              {new Date(complaint.created_at).toLocaleDateString()} at{' '}
                              {new Date(complaint.created_at).toLocaleTimeString()}
                            </div>
                          </div>

                          {complaint.resolution_notes && (
                            <div className="mt-3 p-3 bg-muted/50 rounded-md">
                              <p className="text-sm font-medium mb-1">Resolution Notes:</p>
                              <p className="text-sm text-muted-foreground">{complaint.resolution_notes}</p>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          {complaint.status !== 'resolved' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleUpdateStatus(complaint.id, 'in_progress')}
                                disabled={complaint.status === 'in_progress'}
                              >
                                Start Progress
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => setSelectedComplaint(complaint)}
                              >
                                Resolve
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Resolution Dialog */}
      <Dialog open={!!selectedComplaint} onOpenChange={(open) => !open && setSelectedComplaint(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Complaint</DialogTitle>
            <DialogDescription>
              Add resolution notes to close this complaint
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="resolution">Resolution Notes</Label>
              <Textarea
                id="resolution"
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Describe how this complaint was resolved..."
                rows={6}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSelectedComplaint(null);
                  setResolutionNotes("");
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleResolve} disabled={isResolving}>
                {isResolving ? 'Resolving...' : 'Mark as Resolved'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
